import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-kit";
import LeafletMap from "@/components/LeafletMap";
import LiveTrackModal from "@/components/LiveTrackModal";
import { Radio, MapPin, Timer, AlertTriangle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "no ping yet";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

export default function FleetLive() {
  const [data, setData] = useState({ count: 0, rides: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [openLive, setOpenLive] = useState(null);

  const load = () => api.get("/fleet/live").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const drivers = useMemo(
    () => data.rides
      .filter((r) => r.last_location)
      .map((r) => ({
        lat: r.last_location.lat, lng: r.last_location.lng,
        rideId: r.ride_id,
        label: `${r.driver?.name || "Driver"} · ${r.patient_name}`,
      })),
    [data]
  );

  const withGps = data.rides.filter((r) => r.last_location);
  const noGps = data.rides.filter((r) => !r.last_location);

  return (
    <div data-testid="fleet-live-page">
      <PageHeader
        subtitle="Operations"
        title="Live Fleet Map"
        action={
          <div className="flex items-center gap-2 text-xs">
            <Radio size={14} className="text-emerald-600 animate-pulse" />
            <span className="text-slate-500">Refreshing every 15s · {withGps.length} live · {noGps.length} awaiting ping</span>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md overflow-hidden">
          {data.rides.length === 0 ? (
            <div className="empty">No trips in progress right now.</div>
          ) : (
            <LeafletMap
              drivers={drivers}
              onDriverClick={(d) => setSelectedId(d.rideId)}
              height={520}
            />
          )}
        </div>

        <aside className="bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col" style={{ maxHeight: 520 }}>
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="font-display font-semibold text-sm text-slate-900">Active trips</div>
            <div className="text-xs text-slate-500 mt-0.5">Click one to open its live view</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {data.rides.length === 0 && <div className="empty">Nothing in progress.</div>}
            {data.rides.map((r) => (
              <button
                key={r.ride_id}
                onClick={() => { setSelectedId(r.ride_id); setOpenLive(r.ride_id); }}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${selectedId === r.ride_id ? "bg-slate-50" : ""}`}
                data-testid={`fleet-item-${r.ride_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-slate-900 truncate">{r.patient_name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.driver?.name || "Unassigned driver"}</div>
                  </div>
                  {r.eta_minutes != null && (
                    <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border bg-sky-50 text-sky-800 border-sky-200 inline-flex items-center gap-1 whitespace-nowrap">
                      <Timer size={10} /> {r.eta_minutes}m
                    </span>
                  )}
                </div>
                <div className="mt-1.5 text-xs text-slate-500 flex items-center gap-1">
                  <MapPin size={11} className="text-rose-600" /> <span className="truncate">{r.dropoff_address}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px]">
                  {r.last_location ? (
                    <span className="text-emerald-700 inline-flex items-center gap-1">
                      <Radio size={9} className="animate-pulse" /> {timeAgo(r.last_location.at)}
                    </span>
                  ) : (
                    <span className="text-slate-400">no GPS yet</span>
                  )}
                  {r.off_route_meters != null && r.off_route_meters > 300 && (
                    <span className="text-rose-700 inline-flex items-center gap-1 font-mono">
                      <AlertTriangle size={10} /> {r.off_route_meters}m off
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </aside>
      </div>

      {openLive && <LiveTrackModal rideId={openLive} mode="live" onClose={() => setOpenLive(null)} />}
    </div>
  );
}
